import { EntryKind } from "../lib/EntryConstants";
import { Entry, Entry as EntryModel } from "../models/Entry";
import { MillenniumDateTime, MillenniumTime } from "@timeedit/millennium-time";
import { TimeConstants } from "./TimeConstants";

import _ from "underscore";
import { AvailabilityEntry } from "../models/AvailabilityEntry";
import {
    TEntryGeometry,
    TEntryStyleDescriptor,
    TSideBySideStyle,
    TSlotWithStylesAndEntry,
} from "../types/models/TEntry";
import { Header } from "../models/Header";
import { TActivity, TMcFluffyActivityData, TTimeSlot, TTimeSlots } from "../types/api/shared";
import Language from "./Language";
import Log from "./Log";

const BORDER_STRETCH = 1;

type TEntryStyle = {
    left: number;
    top: number;
    width: number;
    height: number;
    cutoffLeft: boolean;
    cutoffRight: boolean;
    cutoffTop: boolean;
    cutoffBottom: boolean;
};

export type TColumnPlacement = {
    column: number;
    colSpan: number;
    numColumns: number;
};

const getColumnPlacements = (
    geometries: TEntryGeometry[],
    isVertical: boolean
): TColumnPlacement[] => {
    const start = (index: number) => (isVertical ? geometries[index].top : geometries[index].left);
    const end = (index: number) =>
        start(index) + (isVertical ? geometries[index].height : geometries[index].width);

    // Entries are stretched over the grid line, so back-to-back entries overlap by BORDER_STRETCH
    const overlaps = (a: number, b: number) => {
        if (start(a) === start(b)) {
            return true;
        }
        const [first, second] = start(a) < start(b) ? [a, b] : [b, a];
        return end(first) - start(second) > BORDER_STRETCH;
    };
    const overlapsAny = (index: number, others: number[]) =>
        others.some((other) => overlaps(index, other));

    const placements: TColumnPlacement[] = [];
    const placeGroup = (group: number[]) => {
        // Placing entries by start time in the first free column uses as few columns as possible
        const columns: number[][] = [];
        group.forEach((index) => {
            const column = columns.find((col) => !overlapsAny(index, col));
            if (column) {
                column.push(index);
            } else {
                columns.push([index]);
            }
        });
        columns.forEach((column, columnIndex) => {
            column.forEach((index) => {
                let colSpan = 1;
                while (
                    columnIndex + colSpan < columns.length &&
                    !overlapsAny(index, columns[columnIndex + colSpan])
                ) {
                    colSpan++;
                }
                placements[index] = { column: columnIndex, colSpan, numColumns: columns.length };
            });
        });
    };

    // Longer entries first among those starting at the same time, so they get the left columns
    const byStart = _.range(geometries.length).sort(
        (a, b) => start(a) - start(b) || end(b) - end(a)
    );
    let group: number[] = [];
    byStart.forEach((index) => {
        if (group.length > 0 && !overlapsAny(index, group)) {
            placeGroup(group);
            group = [];
        }
        group.push(index);
    });
    placeGroup(group);
    return placements;
};

const timeSlotToEntries = (
    slot: TTimeSlot,
    duplicateForObjects = true,
    inHeaderObjects: any[] | null = null,
    props = { periodValues: [], visibleDates: [] },
    getStyleDescriptor: (
        entry: EntryModel | AvailabilityEntry,
        sideBySideStyles?: TSideBySideStyle[]
    ) => TEntryStyleDescriptor | null = _.noop as any
) => {
    const datesForWeekday = (dates, weekday: number) =>
        _.filter(dates, (date) => date.getDay() === weekday);

    const toEntries = (slt: TTimeSlot, dates) => {
        const endTimes = slt.end_times ? slt.end_times : [slt.end_time];
        if (endTimes === undefined || endTimes[0] === undefined) {
            // If landing here, end times from the rule should be used.
            return [];
        }
        let headerObjects = inHeaderObjects ? inHeaderObjects.map((ho) => ho.objects) : [];
        if (headerObjects.length > 0) {
            headerObjects = _.combineArrays(headerObjects);
        }
        let result = _.flatten<Entry[]>(
            dates.map((date) =>
                _.flatten<Entry[]>(
                    endTimes.map((endTime) => {
                        const entry = new Entry(
                            [new MillenniumDateTime(date, new MillenniumTime(slt.start_time))],
                            [
                                new MillenniumDateTime(
                                    date,
                                    new MillenniumTime(
                                        endTime === TimeConstants.SECONDS_PER_DAY
                                            ? endTime - 1
                                            : endTime
                                    )
                                ),
                            ]
                        );
                        entry.periods = props.periodValues;
                        return entry;
                    })
                )
            )
        ).filter((entry) => entry !== undefined);
        if (duplicateForObjects && headerObjects.length > 0 && result.length > 0) {
            result = _.flatten(
                headerObjects.map((ho) =>
                    result.map((entry: Entry) => {
                        const headerEntry = entry.clone();
                        headerEntry.objects = ho;
                        return headerEntry;
                    })
                )
            );
        }
        return _.filter(
            result,
            (entry) => entry.startTimes[0].getMts() < entry.endTimes[0].getMts()
        );
    };

    return _.flatten(
        slot.week_days.map((weekday) => {
            const entries = toEntries(
                slot,
                slot.date ? [slot.date] : datesForWeekday(props.visibleDates, weekday)
            );
            return entries
                .map((entry) => {
                    const result: Partial<TSlotWithStylesAndEntry> | null =
                        getStyleDescriptor(entry);
                    if (!result) {
                        return null;
                    }
                    result.entry = entry;
                    return result as TSlotWithStylesAndEntry;
                })
                .filter((result): result is TSlotWithStylesAndEntry => result !== null);
        })
    );
};

// allEntries includes the entry currently being processed
const isObscured = (entry, allEntries) => {
    if (entry.model.overlapCount < 2 || !entry.model.overlaps) {
        return false;
    }
    const styleOverlaps = (style1, style2) => {
        const max1x = style1.top + style1.height;
        const max1y = style1.left + style1.width;
        const max2x = style2.top + style2.height;
        const max2y = style2.left + style2.width;

        return !(
            max1x <= style2.top ||
            style1.top >= max2x ||
            style1.left >= max2y ||
            max1y <= style2.left
        );
    };

    const stylesOverlap = (styles1, styles2) =>
        styles1.some((style) => styles2.some((st) => styleOverlaps(style, st)));

    const overlappers = allEntries
        .filter((etr) => stylesOverlap(entry.styles, etr.styles))
        .sort((e1, e2) => e1.model.getLength() - e2.model.getLength());
    return entry !== overlappers[0];
};

const EntryUtils = {
    BORDER_STRETCH,
    isObscured,
    getColumnPlacements,

    getTimeSlotsForEntries: (
        slotData: TTimeSlot[],
        duplicateForObjects = true,
        headerObjects: Header[] | null = null,
        props: any | null = null,
        getStyleDescriptor: (
            entry: EntryModel | AvailabilityEntry,
            sideBySideStyles?: TSideBySideStyle[]
        ) => TEntryStyleDescriptor | null = _.noop as any
    ): TSlotWithStylesAndEntry[] => {
        const result = _.flatten(
            slotData.map((slot) =>
                timeSlotToEntries(
                    slot,
                    duplicateForObjects,
                    headerObjects,
                    props,
                    getStyleDescriptor
                )
            )
        );
        return result;
    },

    getEntryKey: (entry, index) => {
        if (entry.kind === EntryKind.NONE) {
            return `ghost${entry.startTimes[0].getMts()}${entry.endTimes[0].getMts()}${entry.reservationids.join(
                ""
            )}`;
        }
        const periods = Object.getOwnPropertyNames(entry.periods).map(
            (period) => `${period}.${entry.periods[period]}`
        );
        let reservationIds = entry.reservationids;
        if (reservationIds.length === 0 || _.every(entry.reservationids, (id) => id === 0)) {
            reservationIds = ["nri", index];
        }
        return `${entry.startTimes[0].getMts()}-${entry.endTimes[0].getMts()}i${reservationIds.join(
            "i"
        )}o${entry.objects.join("o")}p${periods.join("p")}c${entry.clusterId || ""}idx${index}`;
    },

    getEntryStyleFromArray: (styleArray: any[], defaultWidth, defaultHeight) => {
        const defaultStyle: TEntryStyle = {
            left: 0,
            top: 0,
            width: defaultWidth,
            height: defaultHeight,
            cutoffLeft: false,
            cutoffRight: false,
            cutoffTop: false,
            cutoffBottom: false,
        };
        const isDefined = <T>(v: T): v is T => v !== null && v !== undefined;

        return styleArray.reduce((previous, current) => {
            // Cut-off left
            if (isDefined(current.left) && current.left < 0) {
                // eslint-disable-next-line no-param-reassign
                current.cutoffLeft = true;
                // eslint-disable-next-line no-param-reassign
                current.width += current.left;
                // eslint-disable-next-line no-param-reassign
                current.left = 0;
            }

            // Cut-off top
            if (isDefined(current.top) && current.top < 0) {
                // eslint-disable-next-line no-param-reassign
                current.cutoffTop = true;
                // eslint-disable-next-line no-param-reassign
                current.height += current.top;
                // eslint-disable-next-line no-param-reassign
                current.top = 0;
            }

            // Cut-off right
            if (
                isDefined(current.left) &&
                isDefined(current.width) &&
                current.left + current.width - BORDER_STRETCH > previous.width
            ) {
                // eslint-disable-next-line no-param-reassign
                current.cutoffRight = true;
                // eslint-disable-next-line no-param-reassign
                current.width = previous.width - current.left - BORDER_STRETCH;
            }

            // Cut-off bottom
            if (
                isDefined(current.top) &&
                isDefined(current.height) &&
                current.top + current.height - BORDER_STRETCH > previous.height
            ) {
                // eslint-disable-next-line no-param-reassign
                current.cutoffBottom = true;
                // eslint-disable-next-line no-param-reassign
                current.height = previous.height - current.top - BORDER_STRETCH;
            }

            return {
                left: isDefined(current.left) ? previous.left + current.left : previous.left,
                top: isDefined(current.top) ? previous.top + current.top : previous.top,
                width: isDefined(current.width) ? current.width : previous.width,
                height: isDefined(current.height) ? current.height : previous.height,
                cutoffLeft: current.cutoffLeft,
                cutoffRight: current.cutoffRight,
                cutoffTop: current.cutoffTop,
                cutoffBottom: current.cutoffBottom,
            };
        }, defaultStyle);
    },

    activityInfoFromTrackMatchingEntry(
        entry: Entry,
        currentTrack: TActivity[]
    ): TMcFluffyActivityData[] {
        const nonScheduledTracks = currentTrack.filter(
            (activity) => activity.reservationId == null
        );

        const weekMatchedActivityData: TMcFluffyActivityData[] = entry.startTimes
            .map((time) => time.getWeek(false, Language.firstDayOfWeek, Language.daysInFirstWeek))
            .map((week) => nonScheduledTracks.find((activity) => activity.week === week))
            .filter((activity): activity is TActivity => activity != null)
            .map((activity) => ({
                activityId: activity.activityId,
                activitySeriesId: activity.activitySeriesId,
                track: activity.activitySeriesId ? activity!.track ?? 0 : 0,
            }));

        if (weekMatchedActivityData.length !== entry.startTimes.length) {
            Log.warning(
                `Not all activities could be matched to entry start times ${JSON.stringify({
                    entryStartTimes: entry.startTimes,
                    currentTrack: currentTrack.map((activity) => ({
                        id: activity.activityId,
                        week: activity.week,
                    })),
                    retVal: weekMatchedActivityData,
                })}`
            );
        }
        return weekMatchedActivityData;
    },
};

export default EntryUtils;
